<template>
  <div
    :class="['zg-hero', {
      [`zg-hero--page-type-${pageType}`]: pageType,
      'zg-hero--with-background': backgroundImage,
      'zg-hero--with-module': $slots['module']
    }]"
  >
    <background
      key="background"
      :page-type="pageType"
      :is-home-page="isHomePage"
      :is-product-page="isProductPage"
      :is-funnel-page="isFunnelPage"
      :is-article-page="isArticlePage"
      :is-partner-page="isPartnerPage"
      :background="backgroundImage"
      :solid-color="!!usps"
      :with-title-spacer="isHomePage && !!title"
    />

    <home-page
      v-if="isHomePage"
      key="home-page"
      :title="title"
    >
      <template #sub-title>
        <slot
          v-if="$slots['sub-title']"
          name="sub-title"
        />
      </template>
    </home-page>

    <product-page
      v-else-if="isProductPage"
      key="product-page"
      :title="title"
      :usps="usps"
      :tooltip-text="tooltipText"
      :has-background="!!backgroundImage"
      :full-width-product-module="fullWidthProductModule"
      :breadcrumbs="breadcrumbs"
    >
      <template #sub-title>
        <slot
          v-if="$slots['sub-title']"
          name="sub-title"
        />
      </template>

      <template #module>
        <slot
          v-if="$slots['module']"
          name="module"
        />
      </template>

      <template #content>
        <slot
          v-if="$slots['content']"
          name="content"
        />
      </template>
    </product-page>

    <content-page
      v-else-if="isContentPage"
      key="content-page"
      :title="title"
      :breadcrumbs="breadcrumbs"
    >
      <template #sub-title>
        <slot
          v-if="$slots['sub-title']"
          name="sub-title"
        />
      </template>

      <template #content>
        <slot
          v-if="$slots['content']"
          name="content"
        />
      </template>
    </content-page>

    <other-page
      v-else-if="isFunnelPage || isArticlePage || isPartnerPage"
      key="other-page"
      :is-funnel-page="isFunnelPage"
      :is-article-page="isArticlePage"
      :is-partner-page="isPartnerPage"
      :title="title"
      :sub-title="subTitle"
      :has-background="!!backgroundImage"
      :status="status"
      :breadcrumbs="breadcrumbs"
    >
      <template #sub-title>
        <slot
          v-if="$slots['sub-title']"
          name="sub-title"
        />
      </template>

      <template #module>
        <slot
          v-if="$slots['module']"
          name="module"
        />
      </template>

      <template #content>
        <slot
          v-if="$slots['content']"
          name="content"
        />
      </template>
    </other-page>

    <tabs
      v-if="showTabs"
      :tabs="tabs"
      :selected-tab="selectedTab"
      @changed-tab="setSelectedTab"
    />

    <template v-if="showTabs">
      <tab
        v-for="(tab, index) in tabs"
        v-show="index === selectedTab"
        :key="index"
        :tab="tab"
      >
        <slot
          :name="`module${index}`"
        />
      </tab>
    </template>
  </div>
</template>

<script>
  import Background from './Background'
  import ContentPage from './ContentPage'
  import HomePage from './HomePage'
  import OtherPage from './OtherPage'
  import ProductPage from './ProductPage'
  import Tab from './Tab'
  import Tabs from './Tabs'

  export default {
    name: 'ZgHero',
    components: {
      Background,
      HomePage,
      ProductPage,
      ContentPage,
      OtherPage,
      Tab,
      Tabs
    },
    props: {
      pageType: {
        type: String,
        required: true,
        validator: value => [
          'home-page',
          'product-page',
          'content-page',
          'funnel-page',
          'article-page',
          'partner-page',
          'content-page'
        ].includes(value)
      },
      title: {
        type: String,
        required: false,
        default: null
      },
      subTitle: {
        type: String,
        required: false,
        default: null
      },
      usps: {
        type: Array,
        default: null
      },
      tooltipText: {
        type: String,
        default: ''
      },
      background: {
        type: Object,
        required: false,
        default: null,
        validator: value => !value || value.src
      },
      tabs: {
        type: Array,
        required: false,
        default: null
      },
      breadcrumbs: {
        type: Array,
        required: false,
        default: () => []
      },
      status: {
        type: Object,
        required: false,
        default: () => {}
      },
      fullWidthProductModule: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data: () => ({
      selectedTab: 0
    }),
    computed: {
      isHomePage () {
        return this.pageType === 'home-page'
      },
      isProductPage () {
        return this.pageType === 'product-page'
      },
      isFunnelPage () {
        return this.pageType === 'funnel-page'
      },
      isArticlePage () {
        return this.pageType === 'article-page'
      },
      isPartnerPage () {
        return this.pageType === 'partner-page'
      },
      isContentPage () {
        return this.pageType === 'content-page'
      },
      showTabs () {
        return this.isHomePage && this.tabs?.length > 0
      },
      backgroundImage () {
        if (this.background) {
          return this.background
        } else if (this.isHomePage) {
          return {}
        }

        return null
      }
    },
    methods: {
      setSelectedTab ({ product, index }) {
        if (this.selectedTab === index) return

        this.$emit('changed-tab', { product, index })
        this.selectedTab = index
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-hero {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }

  // ? ----------------------------------------------------------------------
  // modifiers
  // ? ----------------------------------------------------------------------

  .zg-hero--page-type-funnel-page,
  .zg-hero--page-type-product-page,
  .zg-hero--page-type-content-page {
    background-color: color(brand-500);
  }

  .zg-hero--page-type-funnel-page {
    background-color: color(beige);
  }

  .zg-hero--page-type-article-page,
  .zg-hero--page-type-partner-page {
    background-color: color(white);
  }

  .zg-hero--page-type-funnel-page + ::v-deep .zg-section,
  .zg-hero--page-type-article-page + ::v-deep .zg-section,
  .zg-hero--page-type-partner-page + ::v-deep .zg-section {
    position: relative;
    margin-top: 0;;
    padding-top: 0;
    background-color: transparent;
    z-index: +1;
  }

  .zg-hero--page-type-product-page {
    &.zg-hero--with-background {
      background-color: transparent;

      &.zg-hero--with-module {
        background-color: color(brand-500);

        @include mq(medium) {
          background-color: color(beige);
        }

        ::v-deep .background {
          .images::before,
          .image,
          .image-1 {
            display: none;

            @include mq(medium) {
              display: block;
            }
          }
        }
      }
    }
  }
</style>
